import { useCallback, useContext } from 'react';
import WheelColumn from './WheelColumn';
import { TimeWheelPickerContext } from '.';
import { formatTo24 } from 'utils/time.util';
import { EMPTY_STRING } from 'constants/general';
import LoadingIndicator from 'components/common/LoadingIndicator';
import {
  TIME_WHEEL_PICKER_DEFAULTS,
  TIME_WHEEL_PICKER_HOURS,
  TIME_WHEEL_PICKER_MINUTES,
  TIME_WHEEL_PICKER_PERIODS,
  TimeWheelPickerColumnType,
  TimeWheelPickerSize,
  TimeWheelPickerVariant
} from 'constants/time-wheel-picker';

interface WheelPickerProps {
  size?: TimeWheelPickerSize;
  variant?: TimeWheelPickerVariant;
  onChange: (arg: string) => void;
  isLoading?: boolean;
}

// Size variants
const sizeClasses = {
  small: 'p-1 border-[0.063rem] shadow',
  medium: 'p-4 border-[0.094rem] shadow-sm',
  large: 'p-6 border-2 shadow-md'
};

// Variant colors
const variantClasses = {
  primary: {
    bg: 'bg-primary-50 dark:bg-secondary-dark-200',
    border: 'border-primary-200 dark:border-secondary-dark-500'
  },
  default: {
    bg: 'bg-gray-100 dark:bg-secondary-dark-200',
    border: 'border-gray-300 dark:border-secondary-dark-500'
  }
};

// Size variants for styling
const loadingSizeClasses = {
  small: '~mx-8/12',
  medium: '~mx-16/20',
  large: '~mx-20/24'
};

const WheelPicker = ({
  size = TIME_WHEEL_PICKER_DEFAULTS.SIZE,
  variant = TIME_WHEEL_PICKER_DEFAULTS.VARIANT,
  onChange,
  isLoading = false
}: WheelPickerProps) => {
  const {
    time: { hour, minute, period },
    setTime
  } = useContext(TimeWheelPickerContext);

  const handleChange = useCallback(
    (type: TimeWheelPickerColumnType, value: string) => {
      const newTime = { hour, minute, period, [type]: value };

      setTime?.(newTime);

      if (newTime.hour && newTime.minute && newTime.period) {
        const time24format = formatTo24(
          `${newTime.hour}:${newTime.minute} ${newTime.period.toLowerCase()}`
        );
        onChange(time24format ?? EMPTY_STRING);
      } else {
        onChange(EMPTY_STRING);
      }
    },
    [hour, minute, period, onChange, setTime]
  );

  const containerClass = `w-fit h-fit ${variantClasses[variant].bg} ${variantClasses[variant].border} rounded-md ${sizeClasses[size]} flex justify-center items-center`;

  if (isLoading) {
    return (
      <div className={containerClass} aria-busy='true'>
        <LoadingIndicator additionalStyles={loadingSizeClasses[size]} />
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <WheelColumn
        items={TIME_WHEEL_PICKER_HOURS}
        value={hour}
        onChange={(value) => {
          handleChange('hour', value);
        }}
        className='border-l rounded-l-md'
        type='hour'
        size={size}
        variant={variant}
      />
      <WheelColumn
        items={TIME_WHEEL_PICKER_MINUTES}
        value={minute}
        onChange={(value) => {
          handleChange('minute', value);
        }}
        type='minute'
        size={size}
        variant={variant}
      />
      <WheelColumn
        items={TIME_WHEEL_PICKER_PERIODS}
        value={period}
        onChange={(value) => {
          handleChange('period', value);
        }}
        className='border-r rounded-r-md'
        type='period'
        size={size}
        variant={variant}
      />
    </div>
  );
};

WheelPicker.displayName = 'WheelPicker';

export default WheelPicker;
